import { AsyncPipe, NgClass, NgStyle } from '@angular/common';
import {
    Component, EventEmitter, Input, Output, ChangeDetectionStrategy, OnChanges, SimpleChanges,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ProductColorInfoComponent } from '../product-color-info/product-color-info.component';

import { MediasPublicConstants } from '@features/products/medias';
import { ProductPublicFunctions, ProductPublicModels } from '@features/products/product';
import { Resolutions } from '@layout/domain/models/layout.models';
import { getResolution } from '@layout/infrastructure/state/layout.selectors';
import { ProductViewConfig } from '@shared/product/models';
import { isProductSizeNotBuyableUseCase } from '@shared/product/use-cases/product-size.use-cases';

@Component({
    selector: 'product-color-selector',
    templateUrl: './product-color-selector.component.html',
    standalone: true,
    imports: [ TranslateModule, AsyncPipe, NgStyle, NgClass, ProductColorInfoComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductColorSelectorComponent implements OnChanges {
    @Input() public selectedColor: ProductPublicModels.PublicProductColor;

    @Input() public colors: ProductPublicModels.PublicProductColor[];

    @Input() public showMoreColors = true;

    @Input() public showColorName = false;

    @Input() public showAllColors = false;

    @Input() public isBulletselector = false;

    @Input() public isHexadecimalColors = false;

    @Input() public textPosition = 'none';

    @Input() public templateConfiguration: ProductViewConfig;

    @Input() public displayReference: string;

    @Input() public isCustomizationColor = false;

    @Input() public classConfig = { wrap: '', button: '' };

    @Input() public designByGroup = false;

    @Output() public readonly colorChangeEvent = new EventEmitter<any>();

    @Output() public readonly checkMoreColors = new EventEmitter<void>();

    public colorsCount: number;

    public moreColors: string;

    public showDesktopColors$: Observable<boolean> = this.store.select(getResolution)
        .pipe(
            map(resolution => resolution !== Resolutions.MOBILE)
        );

    public showColorBullets$: Observable<boolean> = this.showDesktopColors$.pipe(
        map((showDesktopColors) => showDesktopColors || this.isBulletselector || this.templateConfiguration?.colorSelector?.type === 'square' || this.totalColors === 0)
    );

    public totalColors = 0;

    public flexDirection = 'column';

    public colorsView;

    constructor(private readonly store: Store) { }

    public ngOnChanges(_changes: SimpleChanges): void {
        // Este componente debería reducir sus cambios, reaccionar al cambio de color o cambio de producto
        // const changeColor = changes.displayReference?.currentValue !== changes.displayReference?.previousValue;
        // if (changes.displayReference?.currentValue !== changes.displayReference?.previousValue) {
        if (!this.templateConfiguration?.colorSelector) {
            this.templateConfiguration = {
                ...this.templateConfiguration,
                colorSelector: {
                    ...this.templateConfiguration?.colorSelector,
                    type: 'square',
                    colorsCount: 1,
                },
            };
        }
        if (!this.isCustomizationColor) {
            this.checkColorsSizeHidden();
        }
        this.initColors();
        this.checktextPosition();
    }

    public changeColor(color: ProductPublicModels.PublicProductColor): void {
        if (this.selectedColor !== color) {
            this.selectedColor = color;
            this.colorChangeEvent.emit(color);
        }
    }

    public getProductColorUrl(color: ProductPublicModels.PublicProductColor): string {
        return ProductPublicFunctions.GetImagesForColor(
            color,
            MediasPublicConstants.PublicMediaLocationType.colorcut,
        )[0]?.path
            || 'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==';
    }

    public goToProductDetails(): void {
        this.checkMoreColors.emit();
    }

    public trackByIndex(index: number): number {
        return index;
    }

    private initColors(): void {
        this.colorsCount = this.templateConfiguration?.colorSelector?.colorsCount || 4;
        this.totalColors = this.colors?.length || 0;

        if (this.showAllColors) {
            this.colorsView = this.colors;
            this.moreColors = null;
            return;
        }

        this.colorsView = this.getInitialColorsView();
        this.moreColors = this.calculateMoreColorsText();
    }

    private getInitialColorsView(): ProductPublicModels.PublicProductColor[] {
        const mutableColors = [...this.colors || []];

        if (this.selectedColor) {
            const selectedIndex = mutableColors.findIndex(color => color.id === this.selectedColor.id);

            if (selectedIndex >= this.colorsCount) {
                const [selectedColor] = mutableColors.splice(selectedIndex, 1);
                mutableColors.unshift(selectedColor);
            }
        }

        return mutableColors.slice(0, this.colorsCount);
    }

    private calculateMoreColorsText(): string | null {
        const remainingColors = this.totalColors - this.colorsCount;
        return remainingColors > 0 ? `+${remainingColors}` : null;
    }

    private checktextPosition(): void {
        switch (this.textPosition) {
            case 'right':
                this.flexDirection = 'row-reverse';
                break;

            default:
                this.flexDirection = 'column';
                break;
        }
    }

    private checkColorsSizeHidden(): void {
        this.colorsView = this.colors?.filter((c) => isProductSizeNotBuyableUseCase(c.sizes, ['HIDDEN'], false));
    }
}
